"use client";

/**
 * Global error boundary — fallback when the root layout itself
 * throws (which is rare, but Next.js requires a top-level recovery
 * surface). Unlike `app/error.tsx`, this one renders its own
 * `<html>` and `<body>` because the layout it would normally inherit
 * isn't available.
 *
 * That also means the brand CSS variables defined in
 * `app/globals.css` may not be loaded — depends on when the error
 * fires. To stay brand-on regardless, we inline the same `oklch()`
 * color values that `globals.css` declares as `--paper`, `--ink`,
 * `--rule`, and `--shark-alert`. Source values cross-referenced
 * with that file so a future palette tweak ports here too.
 *
 * Otherwise the visual layout mirrors `app/error.tsx`: hairline-
 * bordered detail card, mono caps eyebrow, square corners,
 * left-aligned column at max-width 36rem.
 */

import { useEffect } from "react";

const IS_DEV = process.env.NODE_ENV === "development";

// Mirrors app/globals.css :root values. Kept in sync by convention —
// no automated check because this page only renders for catastrophic
// failures where shipping a separate stylesheet is the wrong move.
const COLORS = {
  paper: "oklch(0.985 0.004 95)",
  paper2: "oklch(0.965 0.005 95)",
  ink: "oklch(0.18 0.012 250)",
  ink2: "oklch(0.36 0.01 250)",
  ink3: "oklch(0.55 0.008 250)",
  rule: "oklch(0.9 0.005 250)",
  alert: "oklch(0.66 0.19 45)",
};

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("Global error:", error);
  }, [error]);

  return (
    <html>
      <body
        style={{
          minHeight: "100vh",
          margin: 0,
          padding: "3rem 1.5rem",
          backgroundColor: COLORS.paper,
          color: COLORS.ink,
          fontFamily:
            'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ width: "100%", maxWidth: "36rem" }}>
          {/* Eyebrow */}
          <div
            style={{
              fontFamily:
                'ui-monospace, "JetBrains Mono", Menlo, Consolas, monospace',
              fontSize: "10px",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: COLORS.alert,
              marginBottom: "1.25rem",
            }}
          >
            Fatal error
          </div>

          {/* Title */}
          <h1
            style={{
              fontSize: "1.5rem",
              fontWeight: 600,
              letterSpacing: "-0.02em",
              margin: 0,
              marginBottom: "1.25rem",
              color: COLORS.ink,
            }}
          >
            Something went wrong
          </h1>

          {/* Body */}
          <p
            style={{
              fontSize: "13px",
              lineHeight: 1.6,
              color: COLORS.ink2,
              maxWidth: "40rem",
              marginBottom: "1.25rem",
            }}
          >
            The app encountered an error it couldn&rsquo;t recover from.
            Try reloading — if it keeps happening, head home and
            we&rsquo;ll investigate.
          </p>

          {/* Error details */}
          {error.message && (
            <div
              style={{
                border: `1px solid ${COLORS.rule}`,
                backgroundColor: COLORS.paper2,
                padding: "0.75rem 1rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  fontFamily:
                    'ui-monospace, "JetBrains Mono", Menlo, Consolas, monospace',
                  fontSize: "10px",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: COLORS.alert,
                  marginBottom: "0.5rem",
                }}
              >
                Error details
              </div>
              <pre
                style={{
                  fontFamily:
                    'ui-monospace, "JetBrains Mono", Menlo, Consolas, monospace',
                  fontSize: "11.5px",
                  lineHeight: 1.55,
                  color: COLORS.ink2,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  margin: 0,
                  maxHeight: "16rem",
                  overflow: "auto",
                }}
              >
                {error.message}
                {IS_DEV && error.stack ? "\n\n" + error.stack : ""}
              </pre>
            </div>
          )}

          {/* Trace */}
          {error.digest && (
            <p
              style={{
                fontFamily:
                  'ui-monospace, "JetBrains Mono", Menlo, Consolas, monospace',
                fontSize: "10.5px",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: COLORS.ink3,
                marginBottom: "1.25rem",
              }}
            >
              Trace ID
              <span
                style={{
                  marginLeft: "0.5rem",
                  fontWeight: 400,
                  letterSpacing: "normal",
                  textTransform: "none",
                  color: COLORS.ink2,
                }}
              >
                {error.digest}
              </span>
            </p>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={reset}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "13px",
                fontWeight: 500,
                backgroundColor: COLORS.ink,
                color: COLORS.paper,
                border: `1px solid ${COLORS.ink}`,
                cursor: "pointer",
              }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => {
                // Send authed-app errors to /dashboard, not "/": on the
                // app host the middleware unconditionally 302s "/" →
                // "/login" without checking the session, so "/" would
                // bounce a logged-in user to login. Read the live path
                // (the router context is gone once the root layout has
                // thrown, so usePathname() isn't reliable here).
                const p =
                  typeof window !== "undefined" ? window.location.pathname : "/";
                const inApp =
                  p.startsWith("/dashboard") || p.startsWith("/staff");
                window.location.href = inApp ? "/dashboard" : "/";
              }}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "13px",
                fontWeight: 500,
                backgroundColor: COLORS.paper,
                color: COLORS.ink,
                border: `1px solid ${COLORS.rule}`,
                cursor: "pointer",
              }}
            >
              Go home
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
